import React, { useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import MyContext from "../ContextApi/MyContext";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../assets/image.webp";
import { useTableState, SortFilterHeader } from "./TableHelpers";

const FLOW_OPTIONS = [
  { value: "", label: "All flows" },
  { value: "login", label: "Login" },
  { value: "forgot_password", label: "Forgot password" },
  { value: "register", label: "Registration" },
];

const OUTCOME_OPTIONS = [
  { value: "", label: "All outcomes" },
  { value: "success", label: "Success" },
  { value: "bad_credentials", label: "Invalid email or password" },
  { value: "not_approved", label: "Account pending approval" },
  { value: "user_not_found", label: "No account for this email" },
  { value: "email_failed", label: "OTP email failed to send" },
  { value: "invalid_otp", label: "Invalid OTP" },
  { value: "expired", label: "OTP expired" },
  { value: "bad_token", label: "Invalid verification token" },
];

const DAYS_OPTIONS = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 0, label: "All time" },
];

// Success reads green, everything else is a failure worth spotting quickly.
const outcomeClass = (outcome) => {
  if (outcome === "success") return "bg-green-100 text-green-800";
  if (outcome === "email_failed") return "bg-red-100 text-red-800";
  if (outcome === "invalid_otp" || outcome === "expired") return "bg-amber-100 text-amber-800";
  return "bg-gray-100 text-gray-700";
};

const LoginAttemptsTable = () => {
  const [attempts, setAttempts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [emailQuery, setEmailQuery] = useState("");
  const [flow, setFlow] = useState("");
  const [outcome, setOutcome] = useState("");
  const [days, setDays] = useState(7);

  const { api } = useContext(MyContext);
  const navigate = useNavigate();
  const admin_token = localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("user"));

  // The typed address differing from the stored one is the signature of a
  // capitalization problem — surface it rather than making someone eyeball it.
  const hasEmailMismatch = (row) =>
    row.registered_email &&
    row.attempted_email &&
    row.attempted_email.trim().toLowerCase() !== row.registered_email.trim().toLowerCase();

  const columns = useMemo(
    () => [
      { key: "time", label: "When", value: (r) => (r.created_at ? new Date(r.created_at).getTime() : null), sortable: true, filterable: false, align: "left" },
      { key: "attempted_email", label: "Email Entered", value: (r) => r.attempted_email || "", sortable: true, filterable: true, align: "left" },
      { key: "flow", label: "Flow", value: (r) => r.flow_display || "", sortable: true, filterable: true, align: "left" },
      { key: "stage", label: "Step", value: (r) => r.stage_display || "", sortable: true, filterable: true, align: "left" },
      { key: "outcome", label: "Result", value: (r) => r.outcome_display || "", sortable: true, filterable: true, align: "left" },
      { key: "ip", label: "IP", value: (r) => r.user_ip || "", sortable: true, filterable: true, align: "left" },
      { key: "detail", label: "Detail", value: (r) => r.detail || "", sortable: false, filterable: false, align: "left" },
    ],
    []
  );

  const { rows, headerPropsFor } = useTableState(attempts, columns, {
    initialSort: { key: "time", direction: "desc" },
  });

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${api}/auth/admin/login-attempts/`, {
        headers: { Authorization: `Token ${admin_token}` },
        params: {
          email: emailQuery.trim() || undefined,
          flow: flow || undefined,
          outcome: outcome || undefined,
          days,
        },
      });
      setAttempts(response.data?.results || []);
      setTotal(response.data?.count || 0);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to load login attempts."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts();
    // Re-query the server whenever a server-side filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow, outcome, days]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <div
      style={{ backgroundImage: `url(${backgroundImage})` }}
      className="min-h-screen p-4 md:p-8 max-w-7xl mx-auto"
    >
      <div className="w-full bg-transparent mb-7">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-4 gap-4">
          <div className="flex items-center gap-3">
            <i
              className="fa-solid fa-arrow-left text-lg text-gray-700 cursor-pointer hover:text-[#0486A5] transition-colors"
              onClick={() => navigate(-1)}
            ></i>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
              Login &amp; OTP Audit
            </h2>
          </div>

          <div className="flex items-center gap-3 mt-3 md:mt-0">
            <h3 className="font-inter text-sm text-black">
              Welcome, {user?.email}!
            </h3>
            <img
              src="/images/Header/img-11.png"
              alt="Profile"
              className="w-8 sm:w-10"
              aria-label="User profile"
            />
            <div onClick={() => handleLogout()}>
              <img
                className="w-6 cursor-pointer"
                src="/images/Header/Vector.png"
                alt="Logout"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search email"
            className="border border-gray-300 px-4 placeholder:text-sm py-1.5 rounded-full text-sm w-full sm:w-64"
            value={emailQuery}
            onChange={(e) => setEmailQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAttempts()}
          />
          <select
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            value={flow}
            onChange={(e) => setFlow(e.target.value)}
          >
            {FLOW_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {DAYS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            className="text-teal-600 hover:text-teal-800 text-sm"
            onClick={() => fetchAttempts()}
          >
            <i className="fa-solid fa-clock-rotate-left mr-1"></i> Refresh
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            Showing {attempts.length} of {total} matching entries
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : attempts.length === 0 ? (
        <div className="text-center text-gray-500">No login attempts found.</div>
      ) : (
        <div className="overflow-auto rounded-lg shadow border border-gray-200">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100 text-gray-700 text-sm font-semibold">
              <tr>
                <SortFilterHeader {...headerPropsFor("time")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("attempted_email")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("flow")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("stage")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("outcome")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("ip")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
                <SortFilterHeader {...headerPropsFor("detail")} thClassName="py-3 px-4 text-sm font-semibold text-gray-700" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t hover:bg-gray-50 transition align-top">
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-800">
                    {row.attempted_email || <span className="text-gray-400">-</span>}
                    {hasEmailMismatch(row) && (
                      <div
                        className="text-xs text-amber-700 mt-0.5"
                        title="The address entered differs from the registered address"
                      >
                        <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                        registered as {row.registered_email}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                    {row.flow_display}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                    {row.stage_display}
                  </td>
                  <td className="py-3 px-4 text-sm whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${outcomeClass(row.outcome)}`}>
                      {row.outcome_display}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                    {row.user_ip || "-"}
                  </td>
                  <td
                    className="py-3 px-4 text-xs text-gray-500 max-w-xs truncate"
                    title={row.detail || ""}
                  >
                    {row.detail || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LoginAttemptsTable;
